'use client';

import { createContext, use, useSyncExternalStore } from 'react';
import type { Command, CommandPullRequest } from './types';

/**
 * Registered commands, kept by group so each registrar replaces only its own. Every registration
 * replaces its group, so the snapshot always holds the latest action closures.
 */
export type CommandStore = {
  register: (groupId: string, commands: readonly Command[]) => void;
  unregister: (groupId: string) => void;
  /** Sets the pull request a registrar's commands act on. Null withdraws it. */
  setTarget: (registrarId: string, target: CommandPullRequest | null) => void;
  subscribe: (listener: () => void) => () => void;
  getSnapshot: () => readonly Command[];
  getTarget: () => CommandPullRequest | null;
};
const NO_COMMANDS: readonly Command[] = [];
export function createCommandStore(): CommandStore {
  const groups = new Map<string, readonly Command[]>();
  const listeners = new Set<() => void>();
  let snapshot = NO_COMMANDS;
  let target: {
    registrarId: string;
    pullRequest: CommandPullRequest;
  } | null = null;

  // A target change leaves the snapshot as it is, so command readers bail out of re-rendering.
  const notify = () => {
    for (const listener of listeners) {
      listener();
    }
  };
  const publish = () => {
    snapshot = [...groups.values()].flat();
    notify();
  };
  return {
    register: (groupId, commands) => {
      const previous = groups.get(groupId) ?? NO_COMMANDS;
      if (previous === commands || previous.length === 0 && commands.length === 0) {
        return;
      }
      groups.set(groupId, commands);
      publish();
    },
    unregister: groupId => {
      if (groups.delete(groupId)) {
        publish();
      }
    },
    setTarget: (registrarId, pullRequest) => {
      // The last registrar wins, and only it withdraws the target, so a page that leaves after
      // the next one registered doesn't clear the next one's.
      if (pullRequest) {
        target = {
          registrarId,
          pullRequest
        };
      } else if (target?.registrarId === registrarId) {
        target = null;
      } else {
        return;
      }
      notify();
    },
    subscribe: listener => {
      listeners.add(listener);
      return () => {
        listeners.delete(listener);
      };
    },
    getSnapshot: () => snapshot,
    getTarget: () => target?.pullRequest ?? null
  };
}

/** Null where no CommandProvider is mounted, such as the desktop tray window. */
export const CommandStoreContext = createContext<CommandStore | null>(null);
const subscribeToNothing = () => () => {};
const getNoCommands = () => NO_COMMANDS;
const getNoTarget = () => null;

/**
 * Hook to read all registered commands, re-rendering when any group changes. Returns an empty
 * list where no CommandProvider is mounted.
 */
export function useCommands() {
  const store = use(CommandStoreContext);
  const getSnapshot = store?.getSnapshot ?? getNoCommands;
  return useSyncExternalStore(store?.subscribe ?? subscribeToNothing, getSnapshot, getSnapshot);
}

/** The pull request the palette's commands act on, as the page registered it. */
export function usePaletteTarget() {
  const store = use(CommandStoreContext);
  const getTarget = store?.getTarget ?? getNoTarget;
  return useSyncExternalStore(store?.subscribe ?? subscribeToNothing, getTarget, getTarget);
}